<template>
  <div id="listWidget" class="mb-5">
    <ul class="breadcrumb">
      <li class="breadcrumb-item">{{ group?.name }}</li>
      <li class="breadcrumb-item active">조명장치 관리</li>
    </ul>
    <!-- 사용 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>현재 방에 조명을 연결하려면 조명제어보드 설정(Setting)모드 스위치를 활성화(Enable) 상태로 해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span>현재방에 연결할 조명의 스위치를 길게 누르면 자동으로 아래에 해당장치가 나타나면, 원하는 장치를 선택후 등록버튼을 클릭해 주세요.</span>
          </div>
        </div>
        <div class="instruction-step" v-if="showBatchConnect">
          <div class="step-number">3</div>
          <div class="step-content">
            <span>처음 연결시, 여러개의 스위치에 일괄로 조명을 연결하려면 '일괄 연결'을 클릭하세요.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="register-card card-item mb-3" v-if="showBatchConnect">
      <div class="register-card-body">
        <button
          type="button"
          class="btn btn-outline-primary w-100"
          :disabled="autoConnecting"
          @click="onAutoConnectClick"
        >
          일괄 연결
        </button>
      </div>
    </div>

    <PopDialog
      title="일괄 연결"
      :visible="autoConnectModal.visible"
      :okCallback="onAutoConnectConfirm"
      :cancelCallback="autoConnectModal.hide"
      okText="연결"
      cancelText="취소"
    >
      <p class="mb-2" style="font-size: 0.85rem; color: #495057;">
        연결할 포트 구간을 입력해주세요.
      </p>
      <select
        v-if="lightingControllerIds.length > 1"
        class="form-control mb-3"
        v-model="autoConnectSerialPick"
      >
        <option
          v-for="id in lightingControllerIds"
          :key="id"
          :value="id"
        >{{ id }}</option>
      </select>
      <p
        v-else-if="autoConnectSerialPick"
        class="mb-2"
        style="font-size: 0.8rem; color: #6c757d;"
      >{{ autoConnectSerialPick }}</p>
      <div class="d-flex align-items-center gap-2">
        <input
          type="number"
          class="form-control"
          inputmode="numeric"
          min="1"
          :max="autoConnectCount"
          v-model.number="autoPortFrom"
        />
        <span>-</span>
        <input
          type="number"
          class="form-control"
          inputmode="numeric"
          min="1"
          :max="autoConnectCount"
          v-model.number="autoPortTo"
        />
      </div>
    </PopDialog>

    <div class="register-card card-item mb-3" v-if="settingModeDevices.length > 0">
      <div class="register-card-header">
        <i class="fa-solid fa-list-check me-2 text-primary"></i>
        <span class="register-card-title">장치 등록</span>
      </div>
      <div class="register-card-body">
        <div class="d-flex align-items-center gap-2">
          <div class="menu-search-input flex-fill">
            <select class="form-control" v-model="deviceName">
              <option value="" style="display: none">장치이름을 선택해주세요.</option>
              <option v-for="index in 99" :key="index" :value="`${index}번`" :disabled="isExistIndex(index)">{{ index }}번</option>
            </select>
          </div>
          <button class="btn btn-primary register-btn" @click="onRegistClick">등록</button>
        </div>
      </div>
    </div>


    <div class="row">
      <LightDeviceItem
        v-for="(item, index) in devices"
        :key="item._id"
        :index="index"
        :data="item"
        v-on:onDeleteClick="onDeleteClick"
        :onSelectChecked="onSelectDeviceChecked"
        :onSelectChange="onSelectDeviceChange"
        :onUpdateDeviceName="onUpdateDeviceName"
        :selectedDevices="selectedDevices"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import Axios, { DeviceApi, GroupApi, ZoneApi } from '@/api';
import {
  useRouter,
  useRoute,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';
import { useStore } from 'vuex';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import LightDeviceItem from './components/LightDeviceItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';
import { buildButtonMapsForController, publishButtonMapsPayload } from '@/common/utils/buttonMaps';
import {
  fromDisplayLampPort,
  lampChannelCount,
  supportsLampCmdCs,
  toDisplayLampPort,
} from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();

const props = defineProps(['data']);
const zone = computed(() => store.getters['home/getCurrentZone']);
const group = ref(undefined);
const deviceName = ref('');
const deviceType = ref(1);
const selectedDevices = ref([]);
const forceRoute = ref(false);
// const addDeviceModal = reactive(useModal());

const settingModeDevices = computed(() => {
  return store.getters['home/getSettingModeDevices'];
});
const autoConnecting = ref(false);
const autoConnectModal = reactive(useModal());
const autoPortFrom = ref(1);
const autoPortTo = ref(12);
const autoConnectSerialPick = ref('');

const controllerIdOf = (controller) =>
  typeof controller === 'string' ? controller : controller?._id;

const lightingControllerIds = computed(() => {
  const ids = [];
  for (const controller of zone.value?.controllers || []) {
    const id = controllerIdOf(controller);
    if (id && supportsLampCmdCs(id) && !ids.includes(String(id))) {
      ids.push(String(id));
    }
  }
  for (const d of group.value?.devices || []) {
    if (d?.type === 1 && !d?.isVirtual && d.controllerId && supportsLampCmdCs(d.controllerId)) {
      const id = String(d.controllerId);
      if (!ids.includes(id)) ids.push(id);
    }
  }
  return ids;
});

const autoConnectCount = computed(() =>
  autoConnectSerialPick.value ? lampChannelCount(autoConnectSerialPick.value) : 12,
);

const roomHasPhysicalLamp = computed(() =>
  (group.value?.devices || []).some((d) => d?.type === 1 && !d?.isVirtual),
);

const showBatchConnect = computed(() => !!group.value && !roomHasPhysicalLamp.value);

const devices = computed(() => {
  let devices = (settingModeDevices.value) || [];
  devices = devices.concat(group.value?.devices?.filter(device => device.type == 1 && !device.isVirtual) || []);
  devices = devices.concat(group.value?.devices?.filter(device => device.isVirtual) || []);
  // devices = devices.filter(device => !device.isVirtual);
  // if(settingModeDevices.value && settingModeDevices.value.length > 0){
  //   settingModeDevices.value.forEach(sd => {
  //     devices.push(sd)
  //   });
  // }
  console.log('DeviceSettingPage.conputed => devices : ', devices);
  return devices;
});

const existIndices = computed(() => {
  const regex = /[^0-9]/g;
  const result = devices?.value?.filter((item) => item.name).map((item) => parseInt(item.name.replace(regex, ""))) || []
  return result;
});

const applyPageHeader = () => {
  emitter.emit('onChangeHeaderSetting', {
    title: '조명장치 관리',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });
};

onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
});

onBeforeRouteLeave((to) => {
  if (forceRoute.value) return true;

  return new Promise((resolve) => {
    emitter.emit('showPopAlert', {
      description: '세팅모드를 종료하시겠습니까?',
      okCallback: async () => {
        forceRoute.value = true;
        store.commit('home/removeAllSettingModeDevices');
        try {
          const payload = { isEditMode: false };
          const result = await Axios.patch(
            ZoneApi.UPDATE_ZONE.replace(':id', zone.value._id),
            payload,
          );
          if (result.resultCode === '0000') {
            mqttControl.onPublishZone(zone.value._id, payload);
            await store.dispatch('home/fetchAllZones');
          }
        } catch (e) {
          console.error(e);
        }
        const target = to.fullPath || to.path;
        if (to.path === '/settings/light/groups') {
          router.back();
        } else {
          await router.replace(target);
        }
      },
      cancelCallback: () => {},
    });
    resolve(false);
  });
});

onMounted(() => {
  // LightGroupsSettingPage onBeforeUnmount 이후 헤더가 기본값으로 돌아가므로 mounted에서 재적용
  applyPageHeader();
  requestGroup();
});

watch(group, () => {
  console.log(
    'DeviceSettingPage.watch => group.devices : ',
    group.value.devices,
  );
});
watch(settingModeDevices.value, () => {
  console.log(
    'DeviceSettingPage.watch => settingModeDevices : ',
    settingModeDevices.value,
  );
});

const isExistIndex = (index) => {
  const result = existIndices?.value?.filter(item => item === index) || [];
  return result.length > 0;
};

const onUpdateDeviceName = (deviceId) => (value) => {
  const device = group.value?.devices?.find(d => d._id === deviceId);
  if (device) {
    device.name = value;
  }
};

const onSelectDeviceChecked = (device) => {
  console.log(device, selectedDevices.value);
  const targetIndex = selectedDevices.value.findIndex(
    (item) =>
      item.serial == device.serial &&
      ((device.data?.port !== undefined && item.data.port == device.data.port) ||
        (device.data.serial && item.data.serial == device.data.serial)),
  );
  return targetIndex >= 0;
};

const onSelectDeviceChange = (device) => {
  const targetIndex = selectedDevices.value.findIndex(
    (item) =>
      item.serial == device.serial &&
      ((device.data.port !== undefined && item.data.port == device.data.port) ||
        (device.data.serial && item.data.serial == device.data.serial)),
  );
  if (targetIndex >= 0) {
    selectedDevices.value = selectedDevices.value.filter(
      (item, index) => index !== targetIndex,
    );
  } else {
    selectedDevices.value.push(device);
  }
  console.log(selectedDevices.value);
};

// const onAddClick = () => {
// addDeviceModal.show();
// requestAddGroup({zoneId:zone.value._id,groupName:'장치'})
// };

// const onAddConfirmClick = () => {
//   console.log(deviceName.value);
//   requestAddDevice({
//     groupId: route.params.groupId,
//     deviceName: deviceName.value,
//   });
// };
const onDeleteClick = (payload) => {
  console.log('DeviceSettingPage.onDeleteZoneClick => payload :', payload);

  const targetDevice = devices.value?.find(device => device._id == payload.deviceId);
  if (targetDevice?.isVirtual && targetDevice?.item?.onoff == 1) {
    emitter.emit('showPopAlert', {
      description: 'ON 상태의 가상버튼은 삭제할 수 없습니다.',
    });
    return;
  }

  const virtualDevices = devices.value?.filter(device => device.isVirtual);
  if (!targetDevice?.isVirtual && virtualDevices.length > 0) {
    emitter.emit('showPopAlert', {
      description: '가상버튼이 있으면 삭제할 수 없습니다.',
    });
    return;
  }

  const hasBoundPhysical =
    !!targetDevice?.isVirtual &&
    (!!targetDevice?.boundButtonAbsorb ||
      (targetDevice?.boundButton?.controllerId != null &&
        targetDevice?.boundButton?.port != null));

  emitter.emit('showPopConfirm', {
    title: '장치 삭제',
    description: hasBoundPhysical
      ? `${payload.name} 장치를 삭제하시겠습니까?\n할당된 물리 버튼이 있으면 먼저 복구한 뒤 삭제합니다.`
      : `${payload.name} 장치를 삭제하시겠습니까?`,
    okText: '삭제',
    cancelText: '취소',
    okCallback: () => {
      requestDeleteDevice(payload.deviceId, targetDevice);
    },
  });
};
const onRegistClick = () => {
  if (selectedDevices.value.length === 0) {
    emitter.emit('showPopAlert', {
      description: '등록할 장치를 선택해주세요.',
    });
    return;
  }
  if (deviceName.value.length === 0) {
    emitter.emit('showPopAlert', {
      description: '장치 이름을 선택해주세요.',
    });
    return;
  }
  const type = selectedDevices.value.at(0).type;
  const otherTypes = selectedDevices.value.filter((item) => item.type !== type);
  if (otherTypes.length > 0) {
    emitter.emit('showPopAlert', {
      description: '같은 타입의 장치만 선택해주세요.',
    });
    return;
  }
  const controllerId = selectedDevices.value.at(0).serial;
  const otherCtrls = selectedDevices.value.filter(
    (item) => item.serial !== controllerId,
  );
  if (otherCtrls.length > 0) {
    emitter.emit('showPopAlert', {
      description: '같은 조명제어기의 장치만 선택해주세요.',
    });
    return;
  }

  const item = {};
  const port = selectedDevices.value.at(0).data.port;
  const serial = selectedDevices.value.at(0).data.serial;
  console.log(port, serial);
  if (port != undefined) {
    item['ports'] = selectedDevices.value.map((item) => item.data.port);
  }
  if (serial) {
    item['serial'] = selectedDevices.value.map((item) => item.data.serial);
  }

  requestAddDevice({
    controllerId: controllerId,
    name: deviceName.value,
    type: type,
    item: item,
  });
  deviceName.value = '';
};

const occupiedDisplayPorts = (serial) => {
  const used = new Set();
  const collect = (devices) => {
    for (const d of devices || []) {
      if (d?.type !== 1 || d?.isVirtual) continue;
      if (String(d.controllerId) !== String(serial)) continue;
      const raw = d.item?.ports;
      const ports = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
      for (const port of ports) {
        const display = Number(toDisplayLampPort(port, serial));
        if (Number.isInteger(display)) used.add(display);
      }
    }
  };
  for (const g of zone.value?.groups || []) collect(g.devices);
  collect(group.value?.devices);
  return used;
};

const defaultAutoConnectSerial = () => {
  const ids = lightingControllerIds.value;
  if (ids.length === 0) return '';
  const roomIds = new Set();
  for (const d of group.value?.devices || []) {
    if (d?.type === 1 && !d?.isVirtual && d.controllerId) {
      roomIds.add(String(d.controllerId));
    }
  }
  const inRoom = ids.filter((id) => roomIds.has(id));
  if (inRoom.length > 0) return inRoom[inRoom.length - 1];
  return ids[ids.length - 1];
};

const onAutoConnectClick = () => {
  if (autoConnecting.value) return;
  const serial = defaultAutoConnectSerial();
  if (!serial) {
    emitter.emit('showPopAlert', {
      description: '연결할 조명제어기가 없습니다.',
    });
    return;
  }
  autoConnectSerialPick.value = serial;
  autoPortFrom.value = 1;
  autoPortTo.value = 12;
  autoConnectModal.show();
};

const onAutoConnectConfirm = () => {
  const serial = autoConnectSerialPick.value;
  const count = lampChannelCount(serial);
  const from = Number(autoPortFrom.value);
  const to = Number(autoPortTo.value);
  if (!serial) {
    emitter.emit('showPopAlert', { description: '연결할 조명제어기가 없습니다.' });
    return;
  }
  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    emitter.emit('showPopAlert', { description: '포트 구간을 숫자로 입력해주세요.' });
    return;
  }
  if (from < 1 || to < 1 || from > count || to > count) {
    emitter.emit('showPopAlert', {
      description: `포트는 1부터 ${count}까지 입력할 수 있습니다.`,
    });
    return;
  }
  if (from > to) {
    emitter.emit('showPopAlert', { description: '시작 포트가 끝 포트보다 클 수 없습니다.' });
    return;
  }
  autoConnectModal.hide();
  requestAutoConnect(serial, from, to);
};

const requestAutoConnect = async (serial, from, to) => {
  autoConnecting.value = true;
  emitter.emit('showLoading');
  try {
    const used = occupiedDisplayPorts(serial);
    let created = 0;
    for (let display = from; display <= to; display += 1) {
      if (used.has(display)) continue;
      const port = fromDisplayLampPort(display, serial);
      if (port == null) continue;
      const result = await Axios.post(
        GroupApi.REGIST_GROUP_DEVICES.replace(':id', route.params.groupId),
        {
          controllerId: serial,
          name: `${display}번`,
          type: 1,
          item: { ports: [port] },
        },
      );
      if (result.resultCode !== '0000') {
        emitter.emit('showPopAlert', {
          description: result.resultMessage || `${display}번 조명 등록에 실패했습니다.`,
        });
        break;
      }
      created += 1;
      const createdDevice = result.resultData?.device;
      if (createdDevice) {
        publishRegisteredLampOn(createdDevice);
        if (zone.value?._id) {
          store.commit('home/addDevice', {
            zoneId: parseInt(zone.value._id),
            groupId: parseInt(route.params.groupId),
            data: createdDevice,
          });
        }
      }
    }
    if (created === 0) {
      emitter.emit('showPopAlert', {
        description: '선택한 구간은 이미 연결되어 있습니다.',
      });
    }
    await requestGroup();
    try {
      await store.dispatch('home/fetchAllZones');
      const maps = await buildButtonMapsForController(
        serial,
        store.getters['home/getZones'],
      );
      publishButtonMapsPayload(mqttControl, serial, maps);
    } catch (e) {
      console.error('button maps push after auto connect failed:', e);
    }
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '일괄 연결 중 오류가 발생하였습니다.',
    });
  } finally {
    autoConnecting.value = false;
    emitter.emit('hideLoading');
  }
};
const requestGroup = async () => {
  try {
    const result = await Axios.get(
      GroupApi.GROUP_INFO.replace(':id', route.params.groupId),
    );
    if (result.resultCode === '0000') {
      group.value = result.resultData;
    } else {
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    }
  } catch (error) {
    emitter.emit('showPopAlert', {
      description: '방 상세 조회에 실패했습니다.',
    });
  }
};
const publishRegisteredLampOn = (device) => {
  if (!device?.controllerId || device._id == null) return;
  mqttControl.onPublish(device.controllerId, device._id, {
    serial: device.controllerId,
    cmd: 'c',
    type: 1,
    data: {
      ports: device.item?.ports,
      onoff: 1,
      bright: device.item?.bright,
      color: device.item?.color ?? 0,
    },
  });
};

const requestAddDevice = async (payload) => {
  try {
    const result = await Axios.post(
      GroupApi.REGIST_GROUP_DEVICES.replace(':id', route.params.groupId),
      payload,
    );
    if (result.resultCode === '0000') {
      const resultDevice = result.resultData.device;
      publishRegisteredLampOn(resultDevice);
      requestGroup();

      if (selectedDevices.value.length > 0) {
        const devices = [...selectedDevices.value];
        devices.forEach((device) =>
          store.dispatch('home/removeSettingModeDevice', device),
        );
        selectedDevices.value = [];
      }
      store.commit('home/addDevice', {
        zoneId: parseInt(zone.value._id),
        groupId: parseInt(route.params.groupId),
        data: result.resultData.device,
      });
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '장치 추가중 오류가 발생하였습니다.',
    });
  }
};
const requestDeleteDevice = async (deviceId, targetDevice = null) => {
  const restoreSerialHint =
    targetDevice?.boundButtonAbsorb?.controllerId ||
    targetDevice?.boundButton?.controllerId ||
    null;
  try {
    const result = await Axios.delete(
      DeviceApi.DELETE_DEVICE.replace(':id', deviceId),
    );
    if (result.resultCode === '0000') {
      requestGroup();
      store.commit('home/deleteDevice', {
        zoneId: parseInt(zone.value._id),
        groupId: parseInt(route.params.groupId),
        deviceId,
      });
      const restore = result.resultData?.restore;
      try {
        await store.dispatch('home/fetchAllZones');
      } catch (e) {
        console.error('fetchAllZones after virtual delete failed:', e);
      }
      const serial = restore?.controllerId || restoreSerialHint;
      if (serial) {
        try {
          const maps = await buildButtonMapsForController(
            serial,
            store.getters['home/getZones'],
          );
          publishButtonMapsPayload(mqttControl, serial, maps);
        } catch (e) {
          console.error('button maps push after virtual delete failed:', e);
        }
      }
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (error) {
    console.log(error)
    emitter.emit('showPopAlert', {
      description: '장치 삭제중 오류가 발생하였습니다.',
    });
  }
};
</script>

<style scoped>
/* instruction-card 스타일 */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #495057;
}

.step-content strong {
  color: #0d6efd;
  font-weight: 600;
}

/* register-card 스타일 (instruction-card와 동일한 룩앤필) */
.register-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.register-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.register-card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.register-card-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.register-card-body {
  padding: 0.75rem 1rem;
}

.register-card-body .menu-search-input .form-control {
  border-radius: 0.5rem;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.register-card-body .menu-search-input .form-control:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.1);
}

.register-card-body .register-btn {
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.2);
  transition: all 0.2s ease;
  font-weight: 500;
  padding: 0.5rem 1.25rem;
  white-space: nowrap;
}

.register-card-body .register-btn:hover {
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.3);
  transform: translateY(-1px);
}

.register-card-body .register-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.2);
}

#listWidget > p code {
  background-color: #fff3cd;
  color: #856404;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.9em;
  font-weight: 500;
}

/* 장치 등록 폼 스타일 개선 */
.flex-fill.mb-1 {
  margin-bottom: 1.5rem;
}

.flex-fill.mb-1 .btn-primary {
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.2);
  transition: all 0.2s ease;
  font-weight: 500;
  padding: 0.5rem 1.25rem;
}

.flex-fill.mb-1 .btn-primary:hover {
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.3);
  transform: translateY(-1px);
}

.flex-fill.mb-1 .btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.2);
}

/* select 박스 스타일 미세 조정 */
.menu-search-input .form-control {
  border-radius: 0.5rem;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.menu-search-input .form-control:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.1);
}

/* 구분선 스타일 */
hr.mb-3 {
  border-color: #e9ecef;
  opacity: 0.6;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

/* 카드 그리드 간격 조정 */
.row {
  gap: 0.75rem;
}

/* 타이포그래피 미세 조정 */
#listWidget {
  line-height: 1.6;
}

#listWidget .breadcrumb {
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

#listWidget .breadcrumb-item.active {
  color: #6c757d;
  font-weight: 500;
}
</style>
